import React, { useCallback, useMemo, useState } from 'react';

import capitalize from 'lodash/capitalize';

import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DataGridPro, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { useGetCurationList } from '~/hooks/apis/bedbank/useCuration';

import { toDayJs } from '~/services/TimeService';

import CurationDetailPanel from './components/CurationDetailPanel';
import {
  ARCHIVE_DECISIONS,
  CLASSIFICATION_FALLBACK_LABEL,
  CLASSIFICATION_FALLBACK_STYLE,
  CLASSIFICATION_LABEL,
  CLASSIFICATION_OPTIONS,
  CLASSIFICATION_STYLE,
  DEFAULT_PAGE_SIZE,
  REVIEW_DECISION_COLOR,
  REVIEW_DECISION_LABEL,
  REVIEW_OPTIONS,
  STATUS_COLOR,
  STATUS_OPTIONS,
  getClassificationTextColor,
} from './constants';
import { CurationClassification, CurationItem, CurationStatus } from './types';

export default function CurationDashboardPage() {
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<CurationStatus | ''>('');
  const [classificationFilter, setClassificationFilter] = useState<CurationClassification | ''>('');
  const [reviewFilter, setReviewFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const { data, isLoading, error } = useGetCurationList({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    status: statusFilter,
    classification: classificationFilter,
  });

  const filteredRows = useMemo(() => {
    if (!data?.result) return [];
    let rows = showArchived
      ? data.result
      : data.result.filter(
          (row) => !ARCHIVE_DECISIONS.includes(row.reviewDecision as (typeof ARCHIVE_DECISIONS)[number]),
        );
    if (reviewFilter === 'pending') {
      rows = rows.filter((row) => !row.reviewDecision);
    } else if (reviewFilter) {
      rows = rows.filter((row) => row.reviewDecision === reviewFilter);
    }
    return rows;
  }, [data?.result, showArchived, reviewFilter]);

  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setStatusFilter(e.target.value as CurationStatus | '');
    setPage(0);
  }, []);

  const handleClassificationChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setClassificationFilter(e.target.value as CurationClassification | '');
    setPage(0);
  }, []);

  const handleReviewChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setReviewFilter(e.target.value);
  }, []);

  const columns: Array<GridColDef> = useMemo(
    () => [
      {
        field: 'propertyName',
        headerName: 'Property Name',
        flex: 2,
        minWidth: 200,
        display: 'flex',
        renderCell: ({ row }: GridRenderCellParams<CurationItem>) => (
          <Link href={`/bedbank/properties/${row.propertyId}`} underline="hover">
            {row.propertyName}
          </Link>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        display: 'flex',
        renderCell: ({ value }: GridRenderCellParams<CurationItem>) => (
          <Chip size="small" label={capitalize(value)} color={STATUS_COLOR[value as CurationStatus]} />
        ),
      },
      {
        field: 'classification',
        headerName: 'Classification',
        width: 150,
        display: 'flex',
        renderCell: ({ value }: GridRenderCellParams<CurationItem>) => {
          const classification = value as CurationClassification | undefined;
          const hasMappedClassification = Boolean(classification && CLASSIFICATION_LABEL[classification]);

          return (
            <Chip
              size="small"
              label={
                hasMappedClassification
                  ? CLASSIFICATION_LABEL[classification as CurationClassification]
                  : CLASSIFICATION_FALLBACK_LABEL
              }
              sx={
                hasMappedClassification
                  ? CLASSIFICATION_STYLE[classification as CurationClassification]
                  : CLASSIFICATION_FALLBACK_STYLE
              }
            />
          );
        },
      },
      {
        field: 'combinedScore',
        headerName: 'Combined Score',
        width: 140,
        display: 'flex',
        renderCell: ({ value, row }: GridRenderCellParams<CurationItem>) => (
          <Typography variant="body2" fontWeight="bold" color={getClassificationTextColor(row.classification)}>
            {value}
          </Typography>
        ),
      },
      {
        field: 'metadataScore',
        headerName: 'Metadata Score',
        width: 130,
        display: 'flex',
        renderCell: ({ value }: GridRenderCellParams<CurationItem>) => <>{value}</>,
      },
      {
        field: 'imageQualityScore',
        headerName: 'Image Score',
        width: 120,
        display: 'flex',
        renderCell: ({ value }: GridRenderCellParams<CurationItem>) => <>{value != null ? value : 'N/A'}</>,
      },
      {
        field: 'scoredAt',
        headerName: 'Scored At',
        width: 160,
        display: 'flex',
        renderCell: ({ value }: GridRenderCellParams<CurationItem>) => (
          <>{value ? toDayJs(value).format('DD MMM YYYY HH:mm') : '—'}</>
        ),
      },
      {
        field: 'reviewDecision',
        headerName: 'Review',
        width: 140,
        display: 'flex',
        renderCell: ({ value }: GridRenderCellParams<CurationItem>) => (
          <Chip
            size="small"
            label={value ? REVIEW_DECISION_LABEL[value] ?? value : 'Pending Review'}
            color={value ? REVIEW_DECISION_COLOR[value] ?? 'default' : 'default'}
            variant={value ? 'filled' : 'outlined'}
          />
        ),
      },
    ],
    [],
  );

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} mb={2}>
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={handleStatusChange}
          sx={{ minWidth: 160 }}
        >
          {STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Classification"
          value={classificationFilter}
          onChange={handleClassificationChange}
          sx={{ minWidth: 180 }}
        >
          {CLASSIFICATION_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Review"
          value={reviewFilter}
          onChange={handleReviewChange}
          helperText={reviewFilter ? 'Filters current page only' : undefined}
          sx={{ minWidth: 160 }}
        >
          {REVIEW_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={<Switch checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} size="small" />}
          label="Show Archived"
          sx={{ ml: 1 }}
        />
      </Stack>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGridPro
          rows={filteredRows}
          columns={columns}
          density="compact"
          pagination
          paginationMode="server"
          rowCount={!showArchived || reviewFilter ? filteredRows.length : data?.count ?? 0}
          paginationModel={{ page, pageSize: DEFAULT_PAGE_SIZE }}
          onPaginationModelChange={({ page: newPage }) => setPage(newPage)}
          getDetailPanelHeight={() => 'auto'}
          getDetailPanelContent={({ row }) => <CurationDetailPanel row={row} />}
          getRowHeight={() => 'auto'}
          slots={{ pagination: GridPagination }}
        />
      )}
    </Box>
  );
}
